import { Dispatch, memo, SetStateAction, useEffect } from "react";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
  Theme,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import {
  DEFAULT_PAGE_SIZE,
  PaginationRange,
  usePagination,
  UsePaginationProps,
} from "./usePagination";

export type PaginationProps = {
  totalCount: number;
  fetchMore?: UsePaginationProps["fetchMore"];
  pageInfo?: UsePaginationProps["pageInfo"];
  setPageItems: Dispatch<SetStateAction<PaginationRange>>;
  customPageSizes?: number[];
  defaultPageSize?: number;
  isFullTextSearch?: boolean;
  sx?: SxProps<Theme>;
  goToFirstPage?: boolean;
};

export const INITIAL_PAGE_RANGE = {
  from: 0,
  to: DEFAULT_PAGE_SIZE,
};

export const ASSET_TABLE_INITIAL_PAGE_RANGE = {
  from: 0,
  to: 50,
};

export const FINDINGS_TABLE_INITIAL_PAGE_RANGE = {
  from: 0,
  to: 50,
};

export const Pagination = memo(function Pagination({
  totalCount,
  fetchMore,
  pageInfo,
  setPageItems,
  customPageSizes,
  defaultPageSize = 10,
  isFullTextSearch,
  sx,
  goToFirstPage,
}: PaginationProps) {
  let pageSizes = customPageSizes ?? [10, 25, 50, 100];
  const { handle, page, disabled } = usePagination({
    pageSizes,
    fetchMore,
    pageInfo,
    totalCount,
    isFullTextSearch,
    goToFirstPage,
    defaultPageSize,
  });

  useEffect(() => {
    setPageItems(page.data);
  }, [page.data]);

  const shouldDisplay = totalCount > pageSizes[0] ? "flex" : "none";

  return (
    <Grid
      container
      size={12}
      sx={[
        {
          py: 2,
          px: 4,
          display: shouldDisplay,
          alignItems: "center",
          justifyContent: "end",
          backgroundColor: "background.light",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {pageSizes.length > 1 && (
        <Grid
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "end",
            gap: 1,
            pb: { xs: 1, sm: 0 },
          }}
          size={{
            xs: 12,
            sm: "auto",
          }}
        >
          <InputLabel id="page-size-select-label">Rows Per Page</InputLabel>
          <FormControl size="small">
            <Select
              labelId="page-size-select-label"
              id="page-size-select"
              variant="outlined"
              value={page.size.toString()}
              onChange={handle.onChange}
              autoWidth
              sx={{
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor: "transparent",
                },
              }}
            >
              {pageSizes.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
      <Grid
        sx={{ display: "flex", alignItems: "center" }}
        size={{
          xs: 12,
          sm: "auto",
        }}
      >
        <Button
          onClick={handle.prev}
          startIcon={<ChevronLeftIcon fontSize="small" />}
          disabled={disabled.prev}
        >
          Prev
        </Button>
        <Button
          onClick={handle.next}
          endIcon={<ChevronRightIcon fontSize="small" />}
          disabled={disabled.next}
        >
          Next
        </Button>
      </Grid>
    </Grid>
  );
});
